import { staleTimes, useQuery } from '@platform/api-client';
import type { Money } from '@platform/design-system';
import type { RequestBody, ResponseBody, ShippingClient, ShippingOperation } from '@platform/openapi';
import { queryKeys, usePageContext } from '../usePageContext.ts';

export type CarrierList = ResponseBody<ShippingOperation<'/admin/carriers', 'get'>>;
export type Carrier = CarrierList['items'][number];
export type CarrierAccount = Carrier['accounts'][number];
export type CarrierAccountBody = RequestBody<ShippingOperation<'/admin/carriers/{code}/account', 'put'>>;

export const CARRIERS_MANAGE_ROLE = 'carriers:manage';

export const CARRIER_SECRET_MAX_LENGTH = 500;
export const CARRIER_SETTING_MAX_LENGTH = 100;

export const accountOf = (carrier: Carrier): CarrierAccount | undefined =>
  carrier.accounts.find((account) => account.subsidiaryId === null) ?? carrier.accounts[0];

export const isConfigurable = (carrier: Carrier): boolean => carrier.status === 'available';

export const canDispatchWith = (carrier: Carrier): boolean =>
  isConfigurable(carrier) && accountOf(carrier)?.enabled === true;

export const customerFeeOf = (carrier: Carrier | undefined): Money | null =>
  carrier === undefined ? null : (accountOf(carrier)?.customerFee ?? null);

export const carrierNameOf = (carriers: readonly Carrier[] | undefined, code: string): string =>
  carriers?.find((carrier) => carrier.code === code)?.name ?? code;

export const acceptsParcelChanges =
  (carriers: readonly Carrier[] | undefined) =>
  (code: string): boolean =>
    carriers?.find((carrier) => carrier.code === code)?.capabilities.update === true;

export const useCarriers = (shipping: ShippingClient) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.carriers(tenantId),
    queryFn: () => shipping.get('/admin/carriers', {}),
    staleTime: staleTimes.settings,
  });
};
